import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { basecampApi, ImportJobStatus } from '@/lib/api';

export interface ImportSummary {
  totalImported: number;
  totalSkipped: number;
  totalErrors: number;
  duration: number;
}

interface Props {
  jobId: string;
  onComplete: (summary: ImportSummary) => void;
  onCancel: () => void;
}

const PHASE_LABELS: Record<string, string> = {
  people: 'People',
  projects: 'Projects',
  todolists: 'Task Lists',
  todos: 'Tasks',
  messages: 'Messages',
  documents: 'Documents',
  calendarEvents: 'Calendar Events',
  comments: 'Comments',
  attachments: 'Attachments',
};

const PHASE_ORDER = [
  'people',
  'projects',
  'todolists',
  'todos',
  'messages',
  'documents',
  'calendarEvents',
  'comments',
  'attachments',
];

export function BasecampImportProgress({ jobId, onComplete, onCancel }: Props) {
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  const { data: job } = useQuery({
    queryKey: ['basecamp-job', jobId],
    queryFn: () => basecampApi.getJobStatus(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'running' || status === 'pending') return 2000;
      return false;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => basecampApi.cancelJob(jobId),
  });

  // Notify parent when complete (don't auto-navigate if there are errors)
  useEffect(() => {
    if ((job?.status === 'completed' || job?.status === 'completed_with_errors') && job.summary) {
      const summary: ImportSummary = {
        totalImported: job.summary.totalImported,
        totalSkipped: job.summary.totalSkipped,
        totalErrors: job.summary.totalErrors,
        duration: job.summary.duration,
      };
      // Only auto-navigate if no errors; otherwise let user review
      if (summary.totalErrors === 0) {
        const timeout = setTimeout(() => onComplete(summary), 1500);
        return () => clearTimeout(timeout);
      }
    }
  }, [job?.status, job?.summary, onComplete]);

  if (!job) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-grey-400 animate-spin mr-2" />
        <span className="text-sm text-grey-500">Loading import status...</span>
      </div>
    );
  }

  const isRunning = job.status === 'running' || job.status === 'pending';
  const isFailed = job.status === 'failed';
  const isCancelled = job.status === 'cancelled';
  const isComplete = job.status === 'completed' || job.status === 'completed_with_errors';
  const hasErrors = job.status === 'completed_with_errors' || isFailed;

  // Calculate overall progress
  const phases = PHASE_ORDER.filter((key) => key in (job.progress || {}));
  const totalItems = phases.reduce((sum, key) => sum + (job.progress[key]?.total || 0), 0);
  const totalProcessed = phases.reduce(
    (sum, key) => sum + (job.progress[key]?.imported || 0) + (job.progress[key]?.skipped || 0),
    0,
  );
  const overallPercent = totalItems > 0 ? Math.round((totalProcessed / totalItems) * 100) : 0;

  // Collect all errors
  const allErrors = phases.flatMap((key) => job.progress[key]?.errors || []);

  // Find the currently active phase
  const activePhase = phases.find((key) => {
    const p = job.progress[key];
    if (!p) return false;
    const processed = p.imported + p.skipped + p.errors.length;
    return processed < p.total;
  });

  // Format duration
  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="space-y-4">
      {/* Status header */}
      <div className="flex items-center gap-3">
        {isRunning && <Loader2 className="w-5 h-5 text-accent animate-spin" />}
        {isComplete && !hasErrors && <CheckCircle className="w-5 h-5 text-success" />}
        {isComplete && hasErrors && <AlertTriangle className="w-5 h-5 text-amber-500" />}
        {isFailed && <XCircle className="w-5 h-5 text-primary" />}
        {isCancelled && <AlertTriangle className="w-5 h-5 text-amber-500" />}
        <div>
          <h3 className="font-medium text-secondary">
            {isRunning && 'Importing...'}
            {isComplete && !hasErrors && 'Import Complete'}
            {isComplete && hasErrors && 'Import Completed with Errors'}
            {isFailed && 'Import Failed'}
            {isCancelled && 'Import Cancelled'}
          </h3>
          {job.summary && (
            <p className="text-xs text-grey-500 mt-0.5">
              {job.summary.totalImported} imported, {job.summary.totalSkipped} skipped
              {job.summary.totalErrors > 0 && `, ${job.summary.totalErrors} errors`}
              {' '}in {formatDuration(job.summary.duration)}
            </p>
          )}
        </div>
      </div>

      {/* Overall progress bar */}
      {isRunning && (
        <div>
          <div className="flex items-center justify-between text-xs text-grey-500 mb-1">
            <span>Overall Progress</span>
            <span>{overallPercent}%</span>
          </div>
          <div className="w-full h-2 bg-grey-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all duration-300"
              style={{ width: `${overallPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Per-phase progress */}
      <div className="border border-grey-200 rounded-lg divide-y divide-grey-100">
        {phases.map((key) => {
          const phase = job.progress[key];
          if (!phase || (phase.total === 0 && key === 'people')) return null;

          const processed = phase.imported + phase.skipped + phase.errors.length;
          const percent = phase.total > 0 ? Math.round((processed / phase.total) * 100) : 0;
          const isActive = key === activePhase && isRunning;
          const isDone = processed >= phase.total && phase.total > 0;

          return (
            <div
              key={key}
              className={`px-4 py-2.5 flex items-center gap-3 ${
                isActive ? 'bg-accent/5' : ''
              }`}
            >
              {/* Status icon */}
              {isDone ? (
                <CheckCircle className="w-4 h-4 text-success shrink-0" />
              ) : isActive ? (
                <Loader2 className="w-4 h-4 text-accent animate-spin shrink-0" />
              ) : (
                <Clock className="w-4 h-4 text-grey-300 shrink-0" />
              )}

              {/* Label */}
              <span className={`text-sm flex-1 ${isActive ? 'font-medium text-secondary' : 'text-grey-600'}`}>
                {PHASE_LABELS[key] || key}
              </span>

              {/* Counts */}
              <span className="text-xs text-grey-400">
                {phase.total > 0 ? (
                  <>
                    {phase.imported > 0 && (
                      <span className="text-success">{phase.imported} imported</span>
                    )}
                    {phase.skipped > 0 && (
                      <span className="ml-1 text-grey-400">{phase.skipped} skipped</span>
                    )}
                    {phase.errors.length > 0 && (
                      <span className="ml-1 text-primary">{phase.errors.length} errors</span>
                    )}
                  </>
                ) : (
                  <span>-</span>
                )}
              </span>

              {/* Mini progress */}
              {isActive && phase.total > 0 && (
                <div className="w-16 h-1.5 bg-grey-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent rounded-full transition-all"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Error details */}
      {allErrors.length > 0 && (
        <div className="border border-primary/20 rounded-lg">
          <button
            className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-primary/5 transition-colors"
            onClick={() => setErrorsExpanded(!errorsExpanded)}
          >
            {errorsExpanded ? (
              <ChevronDown className="w-4 h-4 text-primary" />
            ) : (
              <ChevronRight className="w-4 h-4 text-primary" />
            )}
            <AlertTriangle className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              {allErrors.length} Error{allErrors.length > 1 ? 's' : ''}
            </span>
          </button>
          {errorsExpanded && (
            <div className="px-4 pb-3 max-h-48 overflow-y-auto">
              {allErrors.map((error, i) => (
                <div key={i} className="text-xs text-grey-600 py-1 border-t border-grey-100 first:border-t-0">
                  <span className="text-grey-400">[{error.resourceType}:{error.basecampId}]</span>{' '}
                  {error.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Failed error message */}
      {isFailed && job.error && (
        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-sm text-primary">{job.error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        {isRunning && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
          >
            Cancel Import
          </Button>
        )}
        {isComplete && allErrors.length > 0 && (
          <Button
            size="sm"
            onClick={() => onComplete({
              totalImported: job.summary?.totalImported || 0,
              totalSkipped: job.summary?.totalSkipped || 0,
              totalErrors: job.summary?.totalErrors || allErrors.length,
              duration: job.summary?.duration || 0,
            })}
          >
            Done
          </Button>
        )}
        {(isFailed || isCancelled) && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            Back to Projects
          </Button>
        )}
      </div>
    </div>
  );
}
